import { useMemo } from 'react';
import { useDocumentSnapshot } from '../../../../../firebase/firestore/hooks';
import { Collections } from '../../../../../firebase/firestore/collections';
import { Candidate } from '../../../../../firebase/firestore/documents/candidate';
import { Position } from '../../../../../firebase/firestore/documents/position';
import VideoResponsePresenter from '../videoResponseQuestions/videoResponsePresenter';

export enum QuestionStateEnum {
  UNDEFINED,
  LOADING,
  READY,
}

export type VideoQuestionState = {
  state: QuestionStateEnum;
  questions?: VideoResponsePresenter;
  initialized: boolean;
};

const useVideoResponseQuestions = (candidate?: Candidate, positionId?: string): VideoQuestionState => {
  const position = useDocumentSnapshot<Position>(Collections.POSITIONS, (candidate?.position ?? positionId) || '');

  return useMemo(() => {
    // 1. Loading state (position not ready)
    if (!position) {
      return {
        state: QuestionStateEnum.LOADING,
        questions: undefined,
        initialized: false,
      };
    }

    const videoResponses = candidate?.videoUploadQuestionResponses ?? [];

    // 2. UNDEFINED state (no responses + not applicable)
    const isUndefined = candidate && !videoResponses.length && (candidate.monetized || !position?.videoUploadQuestions?.length);

    if (isUndefined) {
      return {
        state: QuestionStateEnum.UNDEFINED,
        questions: undefined,
        initialized: true,
      };
    }

    // 3. READY state
    // For monetized candidates, only show questions they have responses for
    if (candidate?.monetized) {
      const monetizedPresenter = new VideoResponsePresenter(
        {
          ...position,
          videoUploadQuestions:
            position.videoUploadQuestions?.filter((q) => candidate.videoUploadQuestionResponses?.some((r) => r.id === q.id)) ??
            [],
        },
        candidate,
      );
      return {
        state: QuestionStateEnum.READY,
        questions: monetizedPresenter,
        initialized: true,
      };
    }
    // 3. READY state (use presenter)
    const presenter = new VideoResponsePresenter(position, candidate);
    return {
      state: QuestionStateEnum.READY,
      questions: presenter,
      initialized: true,
    };
  }, [candidate, position]);
};

export default useVideoResponseQuestions;
